import classNames from 'classnames';
import { EmitterSource } from 'quill';
import Delta from 'quill-delta';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAppDispatch } from 'app/hooks';
import Icon from 'components/icons/Icon';
import { IconSymbol, IconVariant } from 'components/icons/Icon.types';
import { useConfirmDelete } from 'components/modal/forms/ConfirmDeleteForm';
import Project from 'components/project/Project';
import ProjectList from 'components/project/ProjectList';
import Publication from 'components/publication/Publication';
import PublicationList from 'components/publication/PublicationList';
import Member from 'components/team/Member';
import Team from 'components/team/Team';
import { getFileURIByFilePath, postFile } from 'features/files/fileAPI';
import { FolderLocation, getStorageFolderName } from 'features/files/fileSlice';
import { IPage } from 'features/page/pageAPI';
import { ISection } from 'features/page/sectionAPI';
import {
  deleteSectionAsync,
  patchSectionAsync,
  postSectionAsync,
} from 'features/page/sectionSlice';
import { canEdit } from 'features/user/userSlice';
import { getFareyOrderUsingIndex } from 'helpers/farey-sorting';
import { AnyOrderObject } from 'helpers/objects';
import Quill from 'helpers/quillSetup';
import { sortByOrder } from 'helpers/sorting';
import { useSite } from 'selectors/siteSelectors';
import { useUser } from 'selectors/userSelectors';
import { usePageContext } from './PageContext';

// import QuillResize from 'quill-resize-module';
// Quill.register('modules/resize', QuillResize);
// this is now done in helpers/quillSetup.ts

type SectionProps = {
  page: IPage;
  section: ISection;
  pageRef?: React.RefObject<HTMLDivElement>;
};

export default function Section(props: SectionProps) {
  const { page, section, pageRef } = props;
  const dispatch = useAppDispatch();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const { user } = useUser();
  const { members, projects, publications } = useSite();

  const currentSection = section;

  const container = useRef(null);
  const editor = useRef<Quill>(null!);
  const isSaved = useRef<boolean>(null!);

  const [hasDataChanged, setHasDataChanged] = useState<boolean>(false);
  const confirmDelete = useConfirmDelete();
  const editable = useMemo(() => {
    if (!section.page) return false;
    return canEdit(user, section.page);
  }, [user, section]);
  const pageContext = usePageContext();
  const isEditing = pageContext?.editing ?? false;

  function handleContentTypeChange(
    event: React.ChangeEvent<HTMLSelectElement>,
  ) {
    const contenttype = event.target.value || null;
    dispatch(
      patchSectionAsync({
        data: {
          ...currentSection,
          contenttype,
          content: null,
        },
      }),
    );
  }

  function handleContentChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const content = event.target.value ? Number(event.target.value) : null;
    dispatch(
      patchSectionAsync({
        data: {
          ...currentSection,
          content,
        },
      }),
    );
  }

  const contentOptions = (() => {
    switch (currentSection.contenttype) {
      case 'Publication':
        return sortByOrder(publications as AnyOrderObject[]).map((item) => ({
          value: item.id,
          label: item.title,
        }));
      case 'Project':
        return sortByOrder(projects as AnyOrderObject[]).map((item) => ({
          value: item.id,
          label: item.title_en || item.title_nl,
        }));
      case 'Member':
        return sortByOrder(members as AnyOrderObject[]).map((item) => ({
          value: item.id,
          label: item.name,
        }));
      default:
        return [];
    }
  })();

  const selectedPublication = publications.find(
    (publication) => publication.id === currentSection.content,
  );
  const selectedProject = projects.find(
    (project) => project.id === currentSection.content,
  );
  const selectedMember = members.find(
    (member) => member.id === currentSection.content,
  );

  useEffect(() => {
    if (container.current == null) return;

    if (editor.current == null) {
      editor.current = new Quill(container.current, {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike', 'blockquote'], // toggled buttons
            // ['blockquote', 'code-block'],
            ['link', 'image'],

            [{ header: 1 }, { header: 2 }], // custom button values
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
            [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
            // [{ direction: 'rtl' }], // text direction

            [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
            [{ header: [1, 2, 3, 4, 5, 6, false] }],

            [{ color: [] }, { background: [] }], // dropdown with defaults from theme
            [{ font: [] }],
            [{ align: [] }],

            ['clean'], // remove formatting button
          ],
          history: {
            userOnly: true,
          },
          resize: {
            modules: ['Resize', 'Toolbar', 'Keyboard', 'DisplaySize'],
            parchment: {
              // Image element configuration
              image: {
                // Adjustable attributes: ['width'] or ['width', 'height']
                attribute: ['width'],
                // Size limits
                limit: {
                  minWidth: 100, // Minimum width
                  maxWidth: 900, // Maximum width
                  minHeight: 100, // Minimum height
                  maxHeight: 900, // Maximum height
                  // ratio: 0.5625, // Width/height ratio (e.g., 16:9 = 0.5625)
                },
              },
              // Similar configuration can be added for other element types
              video: {
                // ...
              },
            },
            tools: [
              'left',
              'center',
              'right',
              'full',
              {
                text: 'Alt', // Button text
                // Custom button attributes
                attrs: {
                  title: 'Set image alt',
                  class: 'btn-alt',
                },
                // Button display condition
                verify(activeEle: { tagName: string }) {
                  return activeEle?.tagName === 'IMG';
                },
                // Button click handler
                handler(
                  evt: Event,
                  button: any,
                  activeEle: {
                    alt: string;
                    setAttribute: (attr: string, value: string) => void;
                  },
                ) {
                  let alt = activeEle.alt || '';
                  alt = window.prompt('Alt for image', alt) ?? '';
                  if (alt) {
                    activeEle.setAttribute('alt', alt);
                  }
                },
              },
            ],
          },
        },
        theme: 'snow',
        formats: quillFormats,
      });

      // Load document text from database if not empty
      if (
        currentSection.text &&
        Object.keys(currentSection.text).length !== 0
      ) {
        try {
          const parsedJson = JSON.parse(currentSection.text);
          const delta = new Delta(parsedJson.content || []);

          // Check and refresh expired image URIs BEFORE setting content
          if (checkExpirationFileURI(delta, 'image')) {
            (async () => {
              const obj = delta;
              await replaceFileURI(obj, 'image');
              editor.current.setContents(new Delta(obj));
              isSaved.current = true;
              setHasDataChanged(false);
            })();
          } else {
            editor.current.setContents(delta);
            isSaved.current = true;
            setHasDataChanged(false);
          }
        } catch (error) {
          console.error('Error loading document text:', error);
        }
      }

      // if (checkExpirationFileURI(editor.current.getContents(), 'image')) save();

      (editor.current.getModule('toolbar') as any).addHandler('image', () => {
        selectLocalImage();
      });

      // Deny pasting of images in the DocBlock
      editor.current.clipboard.addMatcher('IMG', (node, delta) => {
        return new Delta().insert('');
      });
      editor.current.clipboard.addMatcher('PICTURE', (node, delta) => {
        return new Delta().insert('');
      });

      // Handler for editor listener on text change
      const textChangehandler = (
        delta: Delta,
        oldDelta: Delta,
        source: EmitterSource,
      ) => {
        if (source === 'api') {
          // console.log('An API call triggered this change.');
        } else if (source === 'user') {
          // console.log('A user action triggered this change.');

          isSaved.current = false;
          setHasDataChanged(true);
        }
      };

      // Editor listeners on text change and selection change
      editor.current.on('text-change', textChangehandler);

      return () => {
        editor.current?.off('text-change', textChangehandler);
      };
    }
  }, []);

  useEffect(() => {
    if (!user || !isEditing) {
      editor.current?.enable(false);
      setDisabled(true);
    } else {
      editor.current?.enable(true);
      setDisabled(false);
    }
    setSaving(false);
  }, [editor, user, isEditing]);

  // Recursive function that deeply searches an object
  // and checks if there any File URIs that are expired,
  // i.e. if the expiry date in the URL is in the past
  // The "isExpired = isExpired || ..." construct is to short-circuit the recursion
  // i.e. stop the recursion if isExpired is already true
  function checkExpirationFileURI(obj: any, keyToSearch: string): boolean {
    let isExpired: boolean = false;

    if (typeof obj === 'object' && obj !== null) {
      for (const key in obj) {
        if (key === keyToSearch) {
          const url = obj[key];

          const expiryDate = extractExpiryDate(url);
          const toDate = new Date(expiryDate!);

          if (new Date() >= toDate) isExpired = true;
        } else if (typeof obj[key] === 'object') {
          isExpired =
            isExpired || checkExpirationFileURI(obj[key], keyToSearch);
        }
      }
    }

    return isExpired;
  }

  // Function to extract encoded expiry date from URL
  function extractExpiryDate(url: string): string | null {
    const regex = /se=(\d{4}-\d{2}-\d{2}T\d{2}%3A\d{2}%3A\d{2}Z)/;
    const match = url.match(regex);

    if (match && match[1]) {
      return decodeURIComponent(match[1]);
    } else {
      return null;
    }
  }

  // Function that searches through the Quill content/Delta
  // and replaces every (old) image URI with a newly refreshed and generated URI
  async function refreshFileURIs() {
    const obj = editor.current?.getContents();
    await replaceFileURI(obj, 'image');
    editor.current?.setContents(new Delta(obj));
  }

  // Recursive function that deeply searches an object
  // and replaces a property's value if the key is the same as the keyToSearch
  // Used to replace old File URIs in a Document with new fresh ones
  // It finds the file_path as a substring inside of the URL,
  // and uses it to do an API call to get a new URI of the File
  async function replaceFileURI(obj: any, keyToReplace: string): Promise<void> {
    if (typeof obj === 'object' && obj !== null) {
      for (const key in obj) {
        if (key === keyToReplace) {
          const url = obj[key];
          const file_path = extractSubstring(url);
          const response = await getFileURIByFilePath(file_path!);

          obj[key] = response;
        } else if (typeof obj[key] === 'object') {
          await replaceFileURI(obj[key], keyToReplace);
        }
      }
    }
  }

  /*
  Function to extract substring from URL
    Regex form:  
    Images/[filename].[extension], 

    [number] can be any sequence of digits
    [filename] can contain letters, digits, spaces, and hyphens,
    [extension] can be jpg, jpeg, png, gif, svg, pdf, doc, docx, xls, xlsx, ppt, pptx, mp4, webm, ogv, avi, mpeg, mpg, mov, mkv, m4v, flv, 3gp, 3g2, or ts.
  */
  function extractSubstring(url: string) {
    // Regular expression to match the substring pattern
    const regex =
      /Images\/[\w\s-]+\.(jpg|jpeg|png|gif|svg|pdf|doc|docx|xls|xlsx|ppt|pptx|mp4|webm|ogv|avi|mpeg|mpg|mov|mkv|m4v|flv|3gp|3g2|ts)/i;
    // Executing the regular expression on the URL
    const match = url.match(regex);
    // If a match is found, return the matched substring, otherwise return null
    return match ? match[0] : null;
  }

  function selectLocalImage() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.click();

    input.onchange = () => {
      if (input.files == null) return;
      const file = input.files[0];

      if (/^image\//.test(file.type)) {
        postFile(file, {
          storage: getStorageFolderName(FolderLocation.Images),
          description: `Image in section ${currentSection.id}`,
          mimeType: file.type,
          size: file.size,
          sectionFiles: [currentSection.id!],
        }).then((response) => {
          insertToEditor(response.data.message);
        });
      } else {
        console.warn('You can only upload images.');
      }
    };
  }

  function insertToEditor(url: string) {
    const range = editor.current!.getSelection();
    editor.current!.insertEmbed(range!.index, 'image', url, 'user');
  }

  const save = useCallback(() => {
    // Check if there are any expired File URIs
    if (checkExpirationFileURI(editor.current?.getContents(), 'image')) {
      (async () => {
        // Refresh old File URIs with new ones gotten from the backend
        await refreshFileURIs();
        saveHelper();
      })();
    } else {
      saveHelper();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddSection = useCallback(() => {
    const sortedSections = sortByOrder(
      (page.sections ?? []) as AnyOrderObject[],
    );
    const i = sortedSections.findIndex((s) => s.id === section.id);
    console.log('sortedSections:', sortedSections);
    console.log('i:', i, section);
    dispatch(
      postSectionAsync({
        data: {
          text: '',
          farey_order: getFareyOrderUsingIndex(sortedSections, -1, i, false),
          page: page,
        },
      }),
    );
  }, [dispatch, page, section]);

  const handleRemoveSection = useCallback(() => {
    function doDelete(): boolean {
      dispatch(
        deleteSectionAsync({
          id: section.id!,
          pageId: section.page?.id!,
        }),
      ).then((response: any) => {
        switch (response.payload?.response.status) {
          case 204:
            break;
          case 500:
          default:
            console.log(response);
        }
      });
      return true;
    }
    confirmDelete({
      prompts: {
        prompt1: <FormattedMessage id="PAGES.SECTIONS.DELETE_CONFIRM" />,
        prompt2: null,
      },
      again: { serious: true },
    }).then((confirmed: boolean) => {
      if (confirmed) doDelete();
    });
  }, [dispatch, section, confirmDelete]);

  // Autosave (using setInterval)
  // Save each 5 seconds if there is new data to be saved
  useEffect(() => {
    let autoSaveInterval: NodeJS.Timeout;

    if (hasDataChanged) {
      // console.log('Data has changed, starting interval loop');
      setSaving(true);

      autoSaveInterval = setInterval(() => {
        // console.log('Save now! (in interval loop)');
        save();
      }, 500);
    }

    return () => {
      // console.log('Clearing interval');
      clearInterval(autoSaveInterval);
    };
  }, [hasDataChanged]);

  // BeforeUnload
  // Show warning as browser pop-up when there are unsaved changes
  useEffect(() => {
    if (!hasDataChanged) return;

    function handleOnBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      // return (event.returnValue = '');
    }

    window.addEventListener('beforeunload', handleOnBeforeUnload, {
      capture: true,
    });

    return () => {
      window.removeEventListener('beforeunload', handleOnBeforeUnload, {
        capture: true,
      });
    };
  }, [hasDataChanged]);

  // Comment a specific line below to disable that feature in the DocBlock
  const quillFormats = [
    'background',
    'bold',
    'color',
    'font',
    'code',
    'italic',
    'link',
    'size',
    'strike',
    'script',
    'underline',
    'blockquote',
    'header',
    'indent',
    'list',
    'align',
    // 'direction',
    'code-block',
    'formula',
    'image',
    'resize-inline',
    'resize-block',
    // 'video',
  ];

  function saveHelper() {
    // console.log('Save called');
    setSaving(true);

    if (editor.current == null) return;

    // Convert editor content to JSON
    const content = editor.current.getContents();
    const jsonString = JSON.stringify({ content });
    // console.log('Editor content converted to JSON:', jsonString);

    dispatch(
      patchSectionAsync({
        data: {
          id: currentSection.id!,
          text: jsonString,
        },
      }),
    );

    isSaved.current = true;
    setHasDataChanged(false);
    setSaving(false);
  }

  function keyUpListener(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.ctrlKey && e.shiftKey && e.key === 'S') {
      save();
    }
  }

  const isEditingSection = editable && isEditing;

  return (
    <div
      className={classNames('section_content', {
        disabled: disabled,
      })}
    >
      <div className="section_container">
        <div
          className="editor_container"
          ref={container}
          onKeyUp={keyUpListener}
        />
        {isEditingSection ? (
          <>
            <select
              name="contenttype"
              value={currentSection.contenttype ?? ''}
              onChange={handleContentTypeChange}
            >
              <option value="">Select content type</option>
              <option value="Publication">Publication</option>
              <option value="PublicationList">Publication List</option>
              <option value="Project">Project</option>
              <option value="ProjectList">Project List</option>
              <option value="Member">Member</option>
              <option value="Team">Team</option>
              <option value="TeamPictures">Team pictures</option>
            </select>
            <select
              name="content-id"
              value={currentSection.content?.toString() ?? ''}
              onChange={handleContentChange}
              disabled={contentOptions.length === 0}
            >
              <option value="">
                {currentSection.contenttype
                  ? 'Select content'
                  : 'Select a content type first'}
              </option>
              {contentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </>
        ) : (
          <>
            {currentSection.contenttype === 'Publication' &&
            selectedPublication ? (
              <Publication publication={selectedPublication} />
            ) : null}
            {currentSection.contenttype === 'Project' && selectedProject ? (
              <Project project={selectedProject} />
            ) : null}
            {currentSection.contenttype === 'Member' && selectedMember ? (
              <Member member={selectedMember} />
            ) : null}
            {currentSection.contenttype === 'PublicationList' ? (
              <PublicationList />
            ) : null}
            {currentSection.contenttype === 'ProjectList' ? (
              <ProjectList />
            ) : null}
            {currentSection.contenttype === 'Team' ? (
              <Team pageRef={pageRef} />
            ) : null}
            {currentSection.contenttype === 'TeamPictures' ? (
              <Team pageRef={pageRef} pictures={true} />
            ) : null}
          </>
        )}
      </div>
      {isEditingSection ? (
        <div className="section_menu">
          <Icon
            className="icon_save"
            symbol={saving ? IconSymbol.cloudSaving : IconSymbol.cloudSaved}
            variant={saving ? IconVariant.dark : IconVariant.normal}
            hintProps={{
              hint: saving ? 'Saving' : 'Saved',
            }}
            onClick={saving ? undefined : save}
            size={18}
            debug
          />
          <Icon
            inline
            className="icon_button"
            symbol={IconSymbol.bin}
            size={18}
            onClick={() => handleRemoveSection()}
            hintProps={{
              hint: 'Delete section',
            }}
          />
          <Icon
            symbol={IconSymbol.plus}
            onClick={() => handleAddSection()}
            size={18}
            hintProps={{ hint: { id: 'PAGES.ADD_SECTION' } }}
          />
        </div>
      ) : null}
    </div>
  );
}
